import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { getHiringForm, submitHiringApplication } from "@/lib/api";
import { emailWarning } from "@/lib/emailCheck";

// Public coach application (no login): app.liftr.info/careers. The LIFTR written application (Q1 why LIFTR,
// Q2 responsibility outside the workout, Q3 consistency, Q4 commitments, Q5 hardest part), then the practical
// details. Scoring happens on the server; ?c=<token> finishes an application started on the landing page.

const STEPS = ["About you", "Your answers", "Commitments", "Hours and details"];
const input = "w-full rounded-xl bg-[#111] border border-[#2a2a2a] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:border-rose-500 focus:outline-none";

const Choice = ({ on, children, onClick, testid, square }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid}
    className={`w-full rounded-xl border px-4 py-3 text-left text-[15px] leading-snug transition ${on ? "border-rose-500 bg-rose-500/15 text-white" : "border-[#2a2a2a] bg-[#111] text-zinc-200"}`}>
    <span className="flex items-start gap-2">
      <span className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center ${square ? "rounded" : "rounded-full"} ${on ? "border-rose-400 bg-rose-500" : "border-zinc-600"}`}>
        {on && <Check className="h-3 w-3 text-white" />}
      </span>
      <span>{children}</span>
    </span>
  </button>
);

const Q = ({ label, hint, children }) => (
  <fieldset className="space-y-2.5">
    <legend className="text-base font-semibold text-zinc-100">{label}</legend>
    {hint && <p className="text-sm text-zinc-500 -mt-1">{hint}</p>}
    {children}
  </fieldset>
);

export default function Careers() {
  const [params] = useSearchParams();
  const token = params.get("c");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(0);
  const [me, setMe] = useState({ name: "", phone: "", email: "", dob: "", area: "", gym_ids: [] });
  const [a, setA] = useState({});
  const [commit, setCommit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    getHiringForm(token).then((d) => {
      setInfo(d);
      if (d.prefill) {
        setMe((m) => ({ ...m, name: d.prefill.name || "", phone: d.prefill.phone || "", email: d.prefill.email || "", gym_ids: d.prefill.gym_ids || [] }));
        setA(d.prefill.answers || {});
      }
      if (d.prefill?.done) setDone({ first_name: (d.prefill.name || "").split(" ")[0], already: true });
    }).catch(() => setErr("This page didn't load. Check your connection and refresh."));
  }, [token]);

  const stepError = () => {
    if (step === 0) {
      if (me.name.trim().split(/\s+/).length < 2) return "Please add your full name.";
      if (me.phone.replace(/\D/g, "").length < 9) return "Please add your phone number with the country code.";
      if (!/^\S+@\S+\.\S+$/.test(me.email.trim())) return "Please add your email.";
      if (!me.dob) return "Please add your date of birth.";
      if (!me.gym_ids.length) return "Pick at least one gym you'd like to work at.";
    }
    if (step === 1 && info.written.some((q) => (a[q.key] || "").trim().length < 15)) return "Please answer each question in a sentence or two.";
    if (step === 3 && (!(a.shifts || []).length || !a.saturdays)) return "Please tell us which hours you can work.";
    return "";
  };

  const next = async () => {
    const e = stepError();
    if (e) { setErr(e); return; }
    setErr("");
    if (step < STEPS.length - 1) { setStep(step + 1); window.scrollTo(0, 0); return; }
    setBusy(true);
    try {
      const source = Object.fromEntries(["utm_source", "utm_campaign", "utm_content", "ad"].map((k) => [k, params.get(k)]).filter(([, v]) => v));
      setDone(await submitHiringApplication({ ...me, answers: a, commitments: commit, source, token }));
      window.scrollTo(0, 0);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Couldn't send. Check the connection and try again."); }
    finally { setBusy(false); }
  };

  const flip = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  if (!info) {
    return <div className="min-h-screen bg-[#0a0a0a] text-zinc-300 flex items-center justify-center p-6 text-center">{err || <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />}</div>;
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="careers-done">
        <div className="mx-auto max-w-lg px-5 pt-8 space-y-5">
          <Brand showText={false} />
          <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center"><Check className="h-8 w-8 text-emerald-400" /></div>
          <h1 className="font-display text-3xl font-extrabold uppercase">Thanks, {done.first_name}</h1>
          <p className="text-lg text-zinc-300">
            {done.already ? "We already have your application. We'll be in touch soon." : "We've got your application. You'll hear back from us within 3 working days either way."}
          </p>
          <p className="text-sm text-zinc-500">Keep an eye on your email and WhatsApp.</p>
        </div>
      </div>
    );
  }

  const cities = [...new Set(info.gyms.map((g) => g.city))];
  const warn = emailWarning(me.email, me.name);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="careers-form">
      <div className="mx-auto max-w-lg px-5 pb-36" style={{ paddingTop: "max(env(safe-area-inset-top), 24px)" }}>
        <Brand showText={false} />
        {step === 0 && (
          <div className="mt-6 space-y-2">
            <h1 className="font-display text-3xl font-extrabold uppercase leading-tight">Coach at LIFTR</h1>
            <p className="text-zinc-400">We hold our coaches to high standards, and we train you to meet them. About 10 minutes. Answer honestly in your own words; we care how you think, not how polished it sounds.</p>
          </div>
        )}
        <div className="mt-6 flex gap-1.5">
          {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-rose-500" : "bg-[#222]"}`} />)}
        </div>
        <p className="mt-3 text-xs uppercase tracking-[0.18em] text-zinc-500">Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>

        <div className="mt-6 space-y-8">
          {step === 0 && (
            <>
              <div className="space-y-3">
                <input className={input} placeholder="Full name" value={me.name} onChange={(e) => setMe({ ...me, name: e.target.value })} data-testid="careers-name" autoComplete="name" />
                <input className={input} placeholder="Phone (WhatsApp), with country code" inputMode="tel" value={me.phone} onChange={(e) => setMe({ ...me, phone: e.target.value })} data-testid="careers-phone" autoComplete="tel" />
                <input className={input} placeholder="Email" inputMode="email" value={me.email} onChange={(e) => setMe({ ...me, email: e.target.value })} data-testid="careers-email" autoComplete="email" />
                {warn && <p className="text-sm text-amber-300">{warn.message}</p>}
                <label className="block space-y-1.5">
                  <span className="text-sm text-zinc-400">Date of birth</span>
                  <input type="date" className={input} value={me.dob} onChange={(e) => setMe({ ...me, dob: e.target.value })} data-testid="careers-dob" />
                </label>
                <input className={input} placeholder="Where do you live? (area, city)" value={me.area} onChange={(e) => setMe({ ...me, area: e.target.value })} data-testid="careers-area" />
              </div>
              <Q label="Which gyms would you like to work at?" hint="Pick all you can reach easily.">
                {cities.map((city) => (
                  <div key={city} className="space-y-2">
                    {cities.length > 1 && <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 pt-1">{city}</div>}
                    {info.gyms.filter((g) => g.city === city).map((g) => (
                      <Choice key={g.id} square testid={`careers-gym-${g.id}`} on={me.gym_ids.includes(g.id)} onClick={() => setMe({ ...me, gym_ids: flip(me.gym_ids, g.id) })}>{g.name}</Choice>
                    ))}
                  </div>
                ))}
              </Q>
            </>
          )}

          {step === 1 && info.written.map((q, i) => (
            <Q key={q.key} label={`${i + 1}. ${q.label}`} hint={q.hint}>
              <textarea rows={5} maxLength={1500} value={a[q.key] || ""} onChange={(e) => setA((x) => ({ ...x, [q.key]: e.target.value }))}
                className={input} data-testid={`careers-${q.key}`} />
            </Q>
          ))}

          {step === 2 && (
            <Q label="As a coach at LIFTR, I understand my role will include the following outside of coaching sessions:" hint="Tick each one you're comfortable with.">
              {info.commitments.map((c) => (
                <Choice key={c} square testid={`careers-commit-${c.slice(0, 10)}`} on={commit.includes(c)} onClick={() => setCommit(flip(commit, c))}>{c}</Choice>
              ))}
            </Q>
          )}

          {step === 3 && info.info.map((q) => (
            <Q key={q.key} label={q.label} hint={q.hint || (q.type === "multi" ? "Pick all that apply." : null)}>
              {q.type === "single" || q.type === "multi" ? q.options.map((o) => (
                <Choice key={o} square={q.type === "multi"} testid={`careers-${q.key}-${o.slice(0, 10)}`}
                  on={q.type === "multi" ? (a[q.key] || []).includes(o) : a[q.key] === o}
                  onClick={() => setA((x) => ({ ...x, [q.key]: q.type === "multi" ? flip(x[q.key] || [], o) : o }))}>{o}</Choice>
              )) : (
                <input type={q.type === "number" ? "number" : "url"} inputMode={q.type === "number" ? "numeric" : "url"} value={a[q.key] || ""}
                  onChange={(e) => setA((x) => ({ ...x, [q.key]: e.target.value }))} className={input} data-testid={`careers-${q.key}`}
                  placeholder={q.type === "number" ? "e.g. 30000" : "https://"} />
              )}
            </Q>
          ))}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-[#1f1f1f] bg-[#0a0a0a]/95 backdrop-blur px-5 pt-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}>
        <div className="mx-auto max-w-lg space-y-2">
          {err && <p className="text-sm text-amber-300" role="alert">{err}</p>}
          <div className="flex gap-2">
            {step > 0 && <button type="button" onClick={() => { setErr(""); setStep(step - 1); }} className="h-12 px-5 rounded-full border border-[#2a2a2a] text-zinc-300">Back</button>}
            <button type="button" onClick={next} disabled={busy} data-testid="careers-next"
              className="h-12 flex-1 rounded-full bg-rose-600 text-white text-base font-semibold disabled:opacity-60">
              {busy ? "Sending…" : step === STEPS.length - 1 ? "Send my application" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
